//https://jsfiddle.net/gL2peqm5/3/
// https://stackoverflow.com/questions/53402234/how-to-detach-a-shape-from-a-group
import { Component, ElementRef, ViewChild } from '@angular/core';
import Konva from 'konva';
import { KeyValue } from '@angular/common';
import { LayerEnum } from "../models/enums/LayerEnum";
import { EditorService } from '../services/editor.service';
import { EditorObject } from '../models/editor-object.model';
import { colissionBehaviourIgnoresColissionWithCheck, hasCollision, isInSnapingRange, rectHasCollision } from './collision-detection.helper';
import { BaseComponent } from '../models/components/BaseComponent';
import { getComponentFromSource, getComponentsFromSource } from '../generics-helper/getComponentFromSource.generic';
import { NotClickableComponent } from '../models/components/commonComponents/NotClickableComponent';
import { ComponentNames } from '../helper/componentNames';
import { LayerComponent } from '../models/components/commonComponents/LayerComponent';
import { WagonContainerComponent } from '../models/components/containerComponents/WagonContainerComponent';
import { ClassCategoryComponent } from '../models/components/containerComponents/ClassCategoryComponent';
import { ContainerBaseComponent } from '../models/components/containerComponents/ContainerBaseComponent';
import { DockingBehaviourComponent } from '../models/components/commonComponents/DockingBehaviourComponent';
import { componentsContain } from '../helper/componentsContain';
import { environment } from 'src/environments/environment';
import { Vector2d } from 'konva/lib/types';
import { FloorComponent } from '../models/components/containerComponents/FloorComponent';
import { KonvaEvent } from './konva-event-constants';

const guide_line_name = "guide-line";
const guide_line_selector = ".guide-line";

@Component({
  selector: 'app-editor-canvas',
  templateUrl: './editor-canvas.component.html',
  styleUrls: ['./editor-canvas.component.scss']
})
export class EditorCanvasComponent {
  private editorObjectClickHandler: any;
  private stageZoomWheelHandler: any;
  private stageScrollWheelHandler: any;
  private selectionGroupDragMoveHandler: any;
  private selectionGroupDragEndHandler: any;
  private selectionGroupContextMenuHandler: any;

  // factor which is used in zooming
  readonly scaleBy: number = 1.5;
  // factor used for the block size during grid alignment
  readonly blockSnapSize: number = 1;
  // factor used for scrolling
  readonly scrollingSteps = 100;
  readonly maxZoom = 1;
  // let minZoom = 0.05;
  readonly minZoom = 0.01;
  readonly desiredMinimumSideTreshholdX = 1000;

  // property indicating the current scale
  currentScale: number = 1;
  selectedEditorObjects: Konva.Group;
  stage: Konva.Stage;
  layers: KeyValue<number, Konva.Layer>[] = [];
  selectedLayerEnum: any;
  selectedSnapingMode: number;
  @ViewChild('container') container: ElementRef<HTMLDivElement>;
  //debug
  absStats = "";
  stats = "";
  debugId: number = 0;

  constructor(public editorService: EditorService) {
    this.editorObjectClickHandler = this.editorObjectOnClick.bind(this);
    this.selectionGroupContextMenuHandler = this.onSelectionGroupContextMenu.bind(this);
    this.selectionGroupDragMoveHandler = this.onSelectionGroupDragMove.bind(this);
    this.selectionGroupDragEndHandler = this.onSelectionGroupDragEnd.bind(this);
    this.stageScrollWheelHandler = this.onStageWheelScroll.bind(this);
    this.stageZoomWheelHandler = this.onStageWheelZoom.bind(this);
  }

  logStats() {
    var groundLayer = this.getLayer(LayerEnum.ground);
    var backgroundLayer = this.getLayer(LayerEnum.background);
    // var scrollLayer = this.getLayer(LayerEnum.scroll);

    var stageAbs = this.stage.getAbsolutePosition();
    var groundAbs = groundLayer.getAbsolutePosition();
    var backgroundAbs = backgroundLayer.getAbsolutePosition();
    // var scrollAbs = scrollLayer.getAbsolutePosition();

    var absStats = "[stageAbs x: " + stageAbs.x + " y: " + stageAbs.y + "]";
    absStats += " [groundLayerAbs x: " + groundAbs.x + " y: " + groundAbs.y + "]";
    absStats += " [backgroundAbs x: " + backgroundAbs.x + " y: " + backgroundAbs.y + "]";
    // absStats += " [scrollAbs x: " + scrollAbs.x + " y: " + scrollAbs.y + "]";

    var stats = "[stage x: " + this.stage.x() + " y: " + this.stage.y() + "]";
    stats += " [groundLayer x: " + groundLayer.x() + " y: " + groundLayer.y() + "]";
    stats += " [backgroundLayer x: " + backgroundLayer.x() + " y: " + backgroundLayer.y() + "]";
    // stats += " [scrollLayer x: " + scrollLayer.x() + " y: " + scrollLayer.y() + "]";

    this.absStats = absStats;
    this.stats = stats;
  }

  public resizeStage() {
    this.debugLog('resizeStage');
    this.stage.width(this.container.nativeElement.clientWidth);
    this.stage.height(this.container.nativeElement.clientHeight);
    this.stage.batchDraw();
  }

  // function used in add command
  public addNewEditorObject(obj: EditorObject) {
    this.debugLog('addNewEditorObject');
    // add object to canvas
    this.addEditorObject(obj);
    this.addObjectEvents(obj);
    // add obj to variant stored in the service
    this.editorService.addObjectToVariant(obj);
    // reset flag after obj was placed on the canvas
    this.editorService.isNewObj = false;
    // run collission detection
    this.globalCollissionDetection();
    // manually redraw since no konva event triggered canvas change
    this.stage.batchDraw();
  }

  // function is used in edit command to update obj components
  public updateEditorObject(obj: EditorObject, components: Array<BaseComponent>) {
    this.debugLog('updateEditorObject');
    // update object after property changes
    obj.updateComponents(components);
    // run collission detection
    this.globalCollissionDetection();
    // manually redraw since no konva event triggered canvas change
    this.stage.batchDraw();
  }

  public dataURLtoBlob(dataurl) {
    this.debugLog('dataURLtoBlob');
    var arr = dataurl.split(','), mime = arr[0].match(/:(.*?);/)[1],
      bstr = atob(arr[1]), n = bstr.length, u8arr = new Uint8Array(n);
    while (n--) {
      u8arr[n] = bstr.charCodeAt(n);
    }
    return new Blob([u8arr], { type: mime });
  }

  private getWagonDimensions(): { width: number; height: number } {
    this.debugLog('getWagonDimensions');
    let result = { width: 0, height: 0 };
    var objects = this.editorService.currentVariant.floors[this.editorService.getCurrentFloor()].elements as Array<EditorObject>;
    objects.forEach(element => {
      var wagon = getComponentFromSource<WagonContainerComponent>(ComponentNames.wagonContainerComponent, element.components);
      if (wagon != null) {
        result.width = element.width();
        result.height = element.height();
      }
    });
    return result;
  }

  private addLayoutDescriptionForScreenshot(description: string) {
    this.debugLog('addLayoutDescriptionForScreenshot');
    const layer = this.getLayer(this.editorService.getCurrentLayer());
    var screenshotDescription = new Konva.Text({
      x: 1000,
      y: -400,
      text: description,
      fontSize: 200,
      fontFamily: 'Calibri',
      fill: 'black',
      id: 'screenshottext'
    });

    layer.add(screenshotDescription);
    layer.batchDraw();
  }

  private removeLayoutDescriptionForScreenshot() {
    this.debugLog('removeLayoutDescriptionForScreenshot');
    let screenshotDescription = this.stage.find('#screenshottext')[0];
    screenshotDescription.destroy();
    this.stage.batchDraw();
  }

  public debug() {
    // can be used to forward debug relevant functions to the editor component
    // console.log("stage: ", this.stage.children[1].children);
    // this.stage.batchDraw();

    // this.layers.forEach(layerEntry => {
    //   var layerPos = layerEntry.value.position();
    //   console.log("[Debug 1] layer position from debug btn", layerPos);
    // });
  }

  public stageToDataURL(floorName: string, layerName: string) {
    return new Promise((resolve) => {
      setTimeout(() => {
        this.debugLog('stageToDataURL');
        var screenshotDescription = this.editorService.getScreenshotDescription(floorName, layerName);
    
        // add screenshot description
        this.addLayoutDescriptionForScreenshot(screenshotDescription);
    
        this.debugLog("[Debug 1] inside reuest animation frame");
        // convert stage to data url
        var dataURL = this.stage.toDataURL();

        this.debugLog("[Debug 1] dataURL : ", dataURL);
    
        this.removeLayoutDescriptionForScreenshot();
        resolve(dataURL);  // Resolve with the screenshot dataURL
      }, 1);  // Small delay to ensure the canvas updates correctly
    });
  }

  getLayerEnumFromObject(obj: EditorObject) {
    this.debugLog('getLayerEnumFromObject');
    let layerComps = getComponentsFromSource<LayerComponent>(ComponentNames.layerComponent, obj.components);

    if (layerComps.length > 1) {
      return LayerEnum.ground;
    } else if (layerComps.length == 1) {
      return this.getLayerEnumByValue(layerComps[0].id);
    }
  }

  getLayerEnumByValue(val: number) {
    this.debugLog('getLayerEnumByValue');
    let layerEnum: LayerEnum = null;
    if (val === LayerEnum.ground) {
      layerEnum = LayerEnum.ground;
    } else if (val = LayerEnum.ceiling) {
      layerEnum = LayerEnum.ceiling;
    }
    return layerEnum;
  }

  // place obj on the current layer of the canvas
  private addEditorObject(obj: EditorObject) {
    this.debugLog('addEditorObject');
    const layer = this.getLayer(this.getLayerEnumFromObject(obj));
    layer.add(obj);
    //redraws Layer as soon as Image is ready 
    obj.loadImage(layer);
  }

  // add events to obj
  private addObjectEvents(obj: EditorObject) {
    this.debugLog('addObjectEvents');
    // Click Event
    // try to get not clickable component from obj
    let notClickable = getComponentFromSource<NotClickableComponent>(ComponentNames.notClickableComponent, obj.components);

    // if not clickable is null add click event to obj
    if (notClickable == null) {
      obj.on(KonvaEvent.click, this.editorObjectClickHandler);
    }
  }

  editorObjectOnClick(event) {
    // button 2 is the right mouse button, which is used for object rotation
    // therefore we early exit here in this function
    if (event.evt.button === 2) {
      return;
    }

    this.debugLog('object - click');
    // set explicit type of event target to be a editor obj
    const currentObject: EditorObject = event.currentTarget as EditorObject;
    // get layer where obj is placed on
    const layer = currentObject.getLayer();

    // single selection
    if (!event.evt.ctrlKey) {
      // if the obj that emits this event is already selected, remove the selection
      if (this.selectedEditorObjects.children && this.selectedEditorObjects.children.length === 1 && this.selectedEditorObjects.children[0]._id === currentObject._id) {
        this.initSelectedItems();
        this.editorService.resetCurrentEditorObjectFromCanvas();
      }
      else {
        // if the obj isn't selected remove selection from previous obj (if there is any) and apply selection to current obj
        this.initSelectedItems();
        this.setSingleSelection(currentObject);
        this.editorService.setCurrentEditorObjectFromCanvas(currentObject);
      }
    } else {
      let currentItems: EditorObject[] = [...this.selectedEditorObjects.children as EditorObject[]];
      currentItems.push(currentObject);
      this.selectMultipleObjects(currentItems);
    }

    // redraw the layer
    layer.batchDraw();
    event.cancelBubble = true;
  }

  public selectMultipleObjects(editorObjects: EditorObject[]) {
    console.log("selectMultipleObjects", editorObjects.length);
    this.initSelectedItems();
    editorObjects.forEach(eo => {
      this.setMultipleSelection(eo);
    });

    if (this.selectedEditorObjects.children.length == 1) {
      let firstObject = this.selectedEditorObjects.children[0] as EditorObject;
      this.editorService.setCurrentEditorObjectFromCanvas(firstObject);
    } else {
      this.editorService.resetCurrentEditorObjectFromCanvas();
    }

    this.stage.batchDraw();
  }

  setSingleSelection(ed: EditorObject) {
    this.debugLog('setSingleSelection');
    this.setSelectionColor(ed);
    this.selectedEditorObjects.add(ed);
  }

  setMultipleSelection(ed: EditorObject) {
    this.debugLog('setMultipleSelection');
    this.setSelectionColor(ed);
    var pos = ed.getAbsolutePosition();
    this.selectedEditorObjects.add(ed);
    ed.setAbsolutePosition(pos);
  }

  public setSelectionColor(e: EditorObject) {
    this.debugLog('setSelectionColor');
    e.isSelected = true;
    if (!e.hasCollision && e.isSelected) {
      this.objectFillOrange(e);
    }
    else if (e.hasCollision && e.isSelected) {
      this.objectFillOrange(e);
    }

    this.stage.batchDraw();
  }

  removeSelectionFromSingleObject(target: EditorObject) {
    this.debugLog('removeSelectionFromSingleObject');
    const layer = this.getLayer(this.getLayerEnumFromObject(target));
    target.isSelected = false;
    if (!target.hasCollision && !target.isSelected) {
      this.objectRemoveFill(target);
    }
    else if (target.hasCollision && !target.isSelected) {
      this.objectFillRed(target);
    }
    var pos = target.getAbsolutePosition();
    target.moveTo(layer);
    target.setAbsolutePosition({ x: Math.round(pos.x), y: Math.round(pos.y) });
  }

  public removeSelectionFromAllObjects() {
    this.debugLog('removeSelectionFromAllObjects');
    if (this.selectedEditorObjects && this.selectedEditorObjects.children) {
      while (this.selectedEditorObjects.children.length > 0) {
        const element = this.selectedEditorObjects.children[0] as EditorObject;
        this.removeSelectionFromSingleObject(element)
        this.alignToGrid(element);
      }
    }
  }

  private objectFillRed(obj: EditorObject) {
    obj.fill('red');
  };

  private objectFillOrange(obj: EditorObject) {
    obj.fill('orange');
  };

  private objectRemoveFill(obj: EditorObject) {
    obj.fill('');
  };

  public debugLog(message?: any, ...optionalParams: any[]) {
    if (!environment.production) {
      if (optionalParams && optionalParams.length === 0) {
        console.log(message);
      }
      else {
        console.log(message, optionalParams);
      }
    }
  }

  alignToGrid(shape: Konva.Node) {
    this.debugLog('alignToGrid');
    let pos = shape.getPosition();
    this.debugLog('old pos before aligning to grid: ', pos);
    let newPos = {
      x: Math.round((pos.x / this.blockSnapSize) * (this.blockSnapSize)),
      y: Math.round((pos.y / this.blockSnapSize) * (this.blockSnapSize))
    };
    this.debugLog('new pos after aligning to grid: ', newPos);
    shape.setPosition(newPos);
    shape.getStage().batchDraw();
  }

  getLayer(layer: LayerEnum): Konva.Layer {
    return this.layers.find(x => x.key == layer).value;
  }

  private round(value, step) {
    step || (step = 1.0);
    var inv = 1.0 / step;
    return Math.round(value * inv) / inv;
  }

  clearCanvas() {
    this.initSelectedItems();
    let groundLayer = this.getLayer(LayerEnum.ground);
    let ceilingLayer = this.getLayer(LayerEnum.ceiling);
    let groundObjects = groundLayer.children.filter(x => x instanceof EditorObject);
    let ceilingObjects = ceilingLayer.children.filter(x => x instanceof EditorObject);

    groundObjects.forEach(element => {
      let target = groundLayer.children.find(x => x == element);
      target.destroy();
    });
    ceilingObjects.forEach(element => {
      let target = ceilingLayer.children.find(x => x == element);
      target.destroy();
    });
  }

  redrawEditor() {
    this.debugLog('redrawEditor');

    this.editorService.getEditorObjects().forEach((element) => {
      element.isSelected = false;
      this.addEditorObject(element);
      this.addObjectEvents(element);
    });
  }

  setSelectedLayer(layer: number) {
    this.selectedLayerEnum = this.getLayerEnumByValue(layer);
  }

  setSnapingMode(mode: number) {
    this.selectedSnapingMode = mode;
  }

  // switch between Layers (currently ground/ceiling Layer of a wagon)
  public changeLayer() {
    this.debugLog('changeLayer');
    let layer = this.selectedLayerEnum;

    this.initSelectedItems();

    const groundLayer = this.getLayer(LayerEnum.ground);
    const ceilingLayer = this.getLayer(LayerEnum.ceiling);
    const groundChilds = groundLayer.getChildren();
    const ceilingChilds = ceilingLayer.getChildren();

    for (let index = 0; index < groundChilds.length; index++) {
      const element = groundChilds[index] as EditorObject;
      // dirty fix to exclude groups
      if (element.components) {
        let container = getComponentFromSource<WagonContainerComponent>(ComponentNames.wagonContainerComponent, element.components);
        let serviceClassComponent = getComponentFromSource<ClassCategoryComponent>(ComponentNames.classCategoryComponent, element.components);
        let floorComponent = getComponentFromSource<FloorComponent>(ComponentNames.floorComponent, element.components);

        if (container == null && serviceClassComponent == null && floorComponent == null) {
          if (layer === LayerEnum.ground) {
            element.opacity(1);
            element.listening(true);
          } else {
            let layerComponents = getComponentsFromSource<LayerComponent>(ComponentNames.layerComponent, element.components);
            if (layerComponents.length == 2) {
              element.opacity(1);
            } else {
              element.opacity(0.3);
              element.listening(false);
            }
          }
        }
      }
    }

    for (let index = 0; index < ceilingChilds.length; index++) {
      const element = ceilingChilds[index] as EditorObject;
      if (layer === LayerEnum.ceiling) {
        element.show();
      } else {
        element.hide();
      }
    }
    this.stage.batchDraw();
  }

  public createStage() {
    if (this.stage !== undefined) {
      this.stage.destroy();
    }
    this.stage = new Konva.Stage({
      container: 'container',
      width: this.container.nativeElement.clientWidth,
      height: this.container.nativeElement.clientHeight,
    });
  }

  public initSelectedItems() {
    this.debugLog('initSelectedItems');
    const layer = this.getLayer(this.selectedLayerEnum);
    if (this.selectedEditorObjects != null) {
      if (this.selectedEditorObjects.children && this.selectedEditorObjects.children.length > 0) {
        this.removeSelectionFromAllObjects();
      }
      this.selectedEditorObjects.remove();
      this.editorService.resetCurrentEditorObjectFromCanvas();
    }

    this.selectedEditorObjects = new Konva.Group({ draggable: true });
    layer.add(this.selectedEditorObjects);
    this.setEventsToSelectedObjects();
    layer.batchDraw();
  }

  private setEventsToSelectedObjects() {
    this.debugLog('setEventsToSelectedObjects');

    this.selectedEditorObjects.on(KonvaEvent.dragmove, this.selectionGroupDragMoveHandler);

    this.selectedEditorObjects.on(KonvaEvent.dragend, this.selectionGroupDragEndHandler);

    this.selectedEditorObjects.on(KonvaEvent.contextmenu, this.selectionGroupContextMenuHandler);
  }

  private onSelectionGroupDragMove(e) {
    this.debugLog('dragmove');
    // if more than 1 object is selected skip snapping currently
    if (this.selectedEditorObjects.children.length > 1) {
      this.groupCollision();
    }
    else {
      // 1. Object snapping
      // the current moved object
      var self = this.selectedEditorObjects.children[0] as EditorObject;

      if (this.selectedSnapingMode == 1) {
        // 1 Filter EditorObjects (only relevant objects for snapping)
        // Objects from current Floor and current layer without classes and wagoncontainer
        var objectsWithDockingBehaviour: Array<EditorObject> = [];
        var filteredObjects = this.getRelevantObjectsForCollissionDetectionInCurrentFloor(this.editorService.getEditorObjects())

        for (let index = 0; index < filteredObjects.length; index++) {
          const editorObject = filteredObjects[index];
          var dockingBehaviourComponent = getComponentFromSource<DockingBehaviourComponent>(ComponentNames.dockingBehaviorComponent, editorObject.components);
          // only objects with docking behaviour
          if (dockingBehaviourComponent != null) {
            objectsWithDockingBehaviour.push(editorObject);
          }
        }
        // 2 
        this.snapToEdge(self, objectsWithDockingBehaviour);
      }
      else {
        // 2. Collision detection visualizing
        // 2.1 Filter EditorObjects (only relevant objects for collisiondetection)
        this.collisionDetection(self, this.getRelevantObjectsForCollissionDetectionInCurrentFloor(this.editorService.getEditorObjects()));
      }
    }
  }

  private onSelectionGroupDragEnd(e) {
    this.debugLog('dragend');
    // After every drag end we reselect the items in the group so positions get updated correctly
    // this approaches solves the issue of copying objects, which than would be copied at a wrong position
    let group = e.target as Konva.Group;
    let selectedItems = [...group.getChildren()] as EditorObject[];
    this.selectMultipleObjects(selectedItems);

    this.destroyGuidelines();
    this.globalCollissionDetection();
  }

  private onSelectionGroupContextMenu(e) {
    this.debugLog('contextmenu');
    e.evt.preventDefault();
    // rotation applys only to single objects for now
    if (this.selectedEditorObjects.children.length > 1)
      return;

    const obj = this.selectedEditorObjects.children[0] as EditorObject;
    obj.rotateCustom(1);

    this.alignToGrid(obj);

    this.globalCollissionDetection();
  }

  private onStageWheelScroll(e) {
    e.evt.preventDefault();
    if (e.evt.ctrlKey && !e.evt.altKey)
      return;

    var scroll = (e.evt.deltaY > 0 ? -this.scrollingSteps : this.scrollingSteps);

    if (e.evt.ctrlKey && e.evt.altKey) {
      this.layers.forEach(layerEntry => {
        layerEntry.value.x(layerEntry.value.x() + scroll * 2)
      });
    }
    else {
      this.layers.forEach(layerEntry => {
        layerEntry.value.y(layerEntry.value.y() + scroll);
      });
    }

    this.layers.forEach(layerEntry => {
      layerEntry.value.batchDraw();
    });
  }

  private onStageWheelZoom(e) {
    if (!e.evt.ctrlKey)
      return;
    if ((e.evt.ctrlKey && e.evt.altKey)) {
      return;
    }

    var oldScale = this.stage.scaleX();
    var pointer = this.stage.getPointerPosition();

    var mousePointTo = {
      x: this.round(pointer.x / oldScale - this.stage.x() / oldScale, 0.01),
      y: this.round(pointer.y / oldScale - this.stage.y() / oldScale, 0.01)
    };

    let positiveZoom = e.evt.deltaY > 0;
    let negativeZoom = e.evt.deltaY < 0;

    // only zoom between described scales
    if (oldScale > this.minZoom && positiveZoom || oldScale < this.maxZoom && negativeZoom) {
      e.evt.preventDefault();
      this.zoom(e.evt.deltaY, oldScale, pointer, mousePointTo);
    }
  }

  destroyGuidelines() {
    this.debugLog('destroyGuidelines');
    let layer = this.getLayer(this.editorService.getCurrentLayer());
    // clear all previous lines on the screen
    layer.find(guide_line_selector).forEach(line => line.destroy());
    layer.batchDraw();
  }

  private groupCollision() {
    this.debugLog('groupCollision');
    var group = this.selectedEditorObjects;
    var groupedObjects = group.getChildren();
    var groupedEditorObjects: EditorObject[] = [];
    groupedObjects.forEach(element => {
      groupedEditorObjects.push((element as EditorObject));
    });

    var collisionRelevantObjects: EditorObject[] = this.getRelevantObjectsForCollissionDetectionInCurrentFloor(this.editorService.getEditorObjects());
    var finalCollisionRelevantObjects: EditorObject[] = collisionRelevantObjects.filter(function (array_el) {
      return groupedEditorObjects.filter(function (anotherOne_el) {
        return anotherOne_el._id == array_el._id;
      }).length == 0
    });
    this.groupCollisionDetection(group, finalCollisionRelevantObjects);
  }


  getRelevantObjectsForCollissionDetectionInCurrentFloor(editorObjects: EditorObject[]) {
    this.debugLog('getRelevantObjectsForCollissionDetectionInCurrentFloor');
    var resultArray: EditorObject[] = [];
    // current layer value
    const currentLayer = this.editorService.getCurrentLayer();

    for (let index = 0; index < editorObjects.length; index++) {
      const editorObject = editorObjects[index];
      var layerComponents = getComponentsFromSource<LayerComponent>(ComponentNames.layerComponent, editorObject.components);
      if (layerComponents != null) {
        // layer filter
        let isInCurrentLayer = false;
        layerComponents.forEach(x => {
          if (x.id == currentLayer) {
            isInCurrentLayer = true;
          }
        });
        if (isInCurrentLayer) {
          // this.debugLog('current object after layer filter',editorObject);
          var containerBaseComponent = getComponentFromSource<ContainerBaseComponent>(ComponentNames.containerBaseComponent, editorObject.components);
          var classContainer = getComponentFromSource<ClassCategoryComponent>(ComponentNames.classCategoryComponent, editorObject.components);
          let floorComponent = getComponentFromSource<FloorComponent>(ComponentNames.floorComponent, editorObject.components);

          if (containerBaseComponent == null && classContainer == null && floorComponent == null) {
            resultArray.push(editorObject);
          }
        }
      }
    }
    return resultArray;
  }

  private groupCollisionDetection(group, otherObjects: EditorObject[]) {
    this.debugLog('groupCollisionDetection');
    let selfRect = group.getClientRect({});
    for (let index = 0; index < otherObjects.length; index++) {
      const other = otherObjects[index];
      if (rectHasCollision(selfRect, other.getClientRect({}))) {
        group.children.forEach(element => {
          this.objectFillRed(element);
          element.hasCollision = true;
        });
        break;
      }
      else {
        group.children.forEach(element => {
          element.hasCollision = false;
          this.objectFillOrange(element);
        });
      }
    }
  }

  public globalCollissionDetection(): boolean {
    this.debugLog('globalCollissionDetection');
    let objects = this.getRelevantObjectsForCollissionDetectionInCurrentFloor(this.editorService.getEditorObjects());
    let hasCollission = false;
    for (let index = 0; index < objects.length; index++) {
      const current = objects[index];
      let internalHasCollission = this.collisionDetection(current, objects);
      if (internalHasCollission) {
        hasCollission = true;
        this.stage.batchDraw();
      }
    }
    return hasCollission;
  }

  private collisionDetection(self: EditorObject, preFilteredEditorObjects: EditorObject[]): boolean {
    this.debugLog('collisionDetection');
    let hasCollission = false;
    for (let index = 0; index < preFilteredEditorObjects.length; index++) {
      const other = preFilteredEditorObjects[index];
      // Skip check on same object
      if (other._id == self._id) { continue; }

      if (colissionBehaviourIgnoresColissionWithCheck(self, other) && hasCollision(self, other)) {
        this.objectFillRed(self);
        hasCollission = true;
        self.hasCollision = true;
        break;
      }
      else {
        self.hasCollision = false;
        if (self.isSelected) {
          this.objectFillOrange(self);
        }
        else {
          this.objectRemoveFill(self);
        }
      }
    }
    return hasCollission;
  }

  public initLayers() {
    this.debugLog('initLayers');
    this.layers = [];

    const backgroundL = new Konva.Layer();
    const background: KeyValue<number, Konva.Layer> = { key: LayerEnum.background, value: backgroundL };
    this.layers.push(background);
    const groundL = new Konva.Layer();
    const ground: KeyValue<number, Konva.Layer> = { key: LayerEnum.ground, value: groundL };
    this.layers.push(ground);
    // var scrollLayer = new Konva.Layer();
    // const scroll: KeyValue<number, Konva.Layer> = { key: LayerEnum.scroll, value: scrollLayer };
    // this.layers.push(scroll);
    const ceilingL = new Konva.Layer();
    const ceiling: KeyValue<number, Konva.Layer> = { key: LayerEnum.ceiling, value: ceilingL };
    this.layers.push(ceiling);

    this.stage.add(backgroundL);
    this.stage.add(groundL);
    this.stage.add(ceilingL);
    // this.stage.add(scrollLayer);
  }

  setScrollingToStage() {
    this.debugLog('setScrollingToStage');
    this.stage.on(KonvaEvent.wheel, this.stageScrollWheelHandler);
  };

  public setZoomingToStage() {
    this.debugLog('setZoomingToStage');
    this.stage.on(KonvaEvent.wheel, this.stageZoomWheelHandler);
  }

  public zooming(deltaY: number) {
    this.debugLog('zooming');
    let oldScale = this.stage.scaleX();
    let center = {
      x: (this.stage.x() / 2),
      y: (this.stage.y() / 2)
    }
    let mousePointTo = {
      x: this.round(center.x / oldScale, 0.01),
      y: this.round(center.y / oldScale, 0.01)
    };
    let positiveZoom = deltaY > 0;
    let negativeZoom = deltaY < 0;

    // only zoom between described scales
    if (oldScale > this.minZoom && positiveZoom || oldScale < this.maxZoom && negativeZoom) {
      this.zoom(deltaY, oldScale, center, mousePointTo);
    }
    this.centerLayerPosition();
    this.stage.batchDraw();
  }

  private zoom(deltaY, oldScale, point, mousePointTo) {
    this.debugLog('zoom');
    this.currentScale =
      this.round((deltaY > 0 ? oldScale / this.scaleBy : oldScale * this.scaleBy), 0.01);

    this.stage.scale({ x: this.currentScale, y: this.currentScale });
    let newPos = {
      x:
        this.round((-(mousePointTo.x - point.x / this.currentScale) *
          this.currentScale), 0.01),
      y:
        this.round((-(mousePointTo.y - point.y / this.currentScale) *
          this.currentScale), 0.01)
    };

    this.stage.position(newPos);
    this.stage.batchDraw();
  }

  setCurrentScale() {
    this.debugLog('setCurrentScale');
    this.currentScale = this.stage.scaleX();
  }

  centerLayerPosition() {
    this.debugLog('setLayerPositions');

    // Get the original width and height
    var originalWidth = this.stage.width();
    var originalHeight = this.stage.height();

    // Get the current scale
    var scaleX = this.stage.scaleX();
    var scaleY = this.stage.scaleY();

    // Calculate the current dimensions
    var scaledStageWidth = originalWidth / scaleX;
    var scaledStageHeight = originalHeight / scaleY;

    let wagonDimensions = this.getWagonDimensions();

    let stageCenterX = scaledStageWidth / 2;
    let stageCenterY = scaledStageHeight / 2;
    let wagonCenterX = wagonDimensions.width / 2;
    let wagonCenterY = wagonDimensions.height / 2;

    let stageCenteredWagonXPosition = stageCenterX - wagonCenterX;
    let stageCenteredWagonYPosition = stageCenterY - wagonCenterY;

    const offsetLayerPosition: Vector2d = { x: stageCenteredWagonXPosition, y: stageCenteredWagonYPosition };

    this.layers.forEach(layerEntry => {
      layerEntry.value.position(offsetLayerPosition);
    });
  }

  public scaleUntilContentIsCentered() {
    // Log the call stack to identify the previous function
    const stack = new Error().stack;
    this.debugLog("[Debug 1] Call stack: ", stack);

    // Optionally, extract the previous function name from the stack
    const stackLines = stack.split('\n');
    if (stackLines.length > 2) {
      const callingFunction = stackLines[2].trim(); // The previous function is at line 2
      this.debugLog("[Debug 1] Called by: ", callingFunction);
    }

    // Get the original width and height
    var originalWidth = this.stage.width();
    var originalHeight = this.stage.height();

    // Get the current scale
    var scaleX = this.stage.scaleX();
    var scaleY = this.stage.scaleY();

    // Calculate the current dimensions
    var scaledStageWidth = originalWidth / scaleX;
    var scaledStageHeight = originalHeight / scaleY;

    let wagonDimensions = this.getWagonDimensions();

    let currentMinimumSideTreshholdX = (scaledStageWidth - wagonDimensions.width) / 2;

    // Recursive scale adjustment logic
    const adjustScale = () => {

      // Update the scaled dimensions
      scaledStageWidth = originalWidth / this.stage.scaleX();
      scaledStageHeight = originalHeight / this.stage.scaleY();
      currentMinimumSideTreshholdX = (scaledStageWidth - wagonDimensions.width) / 2;

      // Keep scaling down until desired threshold is reached
      if (this.desiredMinimumSideTreshholdX > currentMinimumSideTreshholdX) {
        this.stage.scale({ x: this.stage.scaleX() - 0.01, y: this.stage.scaleY() - 0.01 });
        this.debugLog("[Debug 1] minimum scale check");
        adjustScale();  // Continue recursive scaling
      }
    };

    // First, adjust scale recursively
    adjustScale();
  }

  public setLayerPositions() {
    this.debugLog('setLayerPositions');

    // Log the call stack to identify the previous function
    const stack = new Error().stack;
    this.debugLog("[Debug 1] Call stack: ", stack);

    // Optionally, extract the previous function name from the stack
    const stackLines = stack.split('\n');
    if (stackLines.length > 2) {
      const callingFunction = stackLines[2].trim(); // The previous function is at line 2
      this.debugLog("[Debug 1] Called by: ", callingFunction);
    }

    // Get the original width and height
    var originalWidth = this.stage.width();
    var originalHeight = this.stage.height();

    // Get the current scale
    var scaleX = this.stage.scaleX();
    var scaleY = this.stage.scaleY();

    // Calculate the current dimensions
    var scaledStageWidth = originalWidth / scaleX;
    var scaledStageHeight = originalHeight / scaleY;

    let wagonDimensions = this.getWagonDimensions();

    // After scaling is completed, adjust the positions (this happens exactly once)
    let stageCenterX = scaledStageWidth / 2;
    let stageCenterY = scaledStageHeight / 2;
    let wagonCenterX = wagonDimensions.width / 2;
    let wagonCenterY = wagonDimensions.height / 2;

    let stageCenteredWagonXPosition = stageCenterX - wagonCenterX;
    let stageCenteredWagonYPosition = stageCenterY - wagonCenterY;

    const offsetLayerPosition = { x: stageCenteredWagonXPosition, y: stageCenteredWagonYPosition };

    this.debugLog("[Debug 1] offset layer position: ", offsetLayerPosition);

    // Update positions exactly once after scaling
    this.layers.forEach(layerEntry => {
      var layerposBeforeChange = layerEntry.value.position();
      this.debugLog("[Debug 1] layer position before change: ", layerposBeforeChange);

      layerEntry.value.position(offsetLayerPosition);
    });
  }


  getSnapableObjectsInRange(currentObject: EditorObject, otherObjects: Array<EditorObject>) {
    this.debugLog('getSnapableObjectsInRange');
    let snapableObjects: Array<EditorObject> = new Array<EditorObject>();
    const snappingRangeX: number = 2000;
    const snappingRangeY: number = 2000;
    let scaledSnappingRangeX = snappingRangeX * this.currentScale;
    let scaledSnappingRangeY = snappingRangeY * this.currentScale;
    for (let index = 0; index < otherObjects.length; index++) {
      const element = otherObjects[index];
      if (isInSnapingRange(currentObject, element, scaledSnappingRangeX, scaledSnappingRangeY)) {
        snapableObjects.push(element);
      }
    }
    return snapableObjects;
  }

  snapToEdge(currentObject: EditorObject, filteredWorldObjects: Array<EditorObject>) {
    this.debugLog('snapToEdge');
    let snapableObjects: Array<EditorObject> = this.filterSnapableObjectsMustDockMustNotDock(currentObject, filteredWorldObjects);
    let snapableObjectsInRange = this.getSnapableObjectsInRange(currentObject, snapableObjects);
    this.destroyGuidelines();

    // find possible snapping lines
    var lineGuideStops = this.getLineGuideStops(currentObject, snapableObjectsInRange);
    // find snapping points of current object
    var itemBounds = this.getObjectSnappingEdges(currentObject);

    // now find where can we snap current object
    var guides = this.getGuides(lineGuideStops, itemBounds);

    // do nothing if no snapping
    if (!guides.length) {
      return;
    }

    this.drawGuides(guides);

    var absPos = currentObject.getAbsolutePosition();
    // now force object position
    guides.forEach((lg) => {
      switch (lg.snap) {
        case 'start': {
          switch (lg.orientation) {
            case 'V': {
              absPos.x = lg.lineGuide + lg.offset;
              break;
            }
            case 'H': {
              absPos.y = lg.lineGuide + lg.offset;
              break;
            }
          }
          break;
        }
        case 'end': {
          switch (lg.orientation) {
            case 'V': {
              absPos.x = lg.lineGuide + lg.offset;
              break;
            }
            case 'H': {
              absPos.y = lg.lineGuide + lg.offset;
              break;
            }
          }
          break;
        }
      }
    });
    currentObject.setAbsolutePosition(absPos);
  }

  filterSnapableObjectsMustDockMustNotDock(currentObject: EditorObject, objects: Array<EditorObject>): Array<EditorObject> {
    this.debugLog('filterSnapableObjectsMustDockMustNotDock');
    // result of objects after filtering must dock and must not dock
    var snapableObjects: Array<EditorObject> = [];

    for (let index = 0; index < objects.length; index++) {
      const snapableObject = objects[index];
      if (snapableObject == currentObject) {
        continue;
      }
      // get docking behaviour of current object
      var dbcCurrent = getComponentFromSource<DockingBehaviourComponent>(ComponentNames.dockingBehaviorComponent, currentObject.components);

      // check must dock and must not dock list
      // either both lists contain no entrys or only one of the lists contains entrys where the object needs to snap to

      // check if there are objects where the current object must dock
      if (dbcCurrent.mustDock.length > 0) {
        var mustDockList = dbcCurrent.mustDock;
        innerloop:
        for (let index = 0; index < mustDockList.length; index++) {
          const mustDockComponent = mustDockList[index];
          if (componentsContain(snapableObject.components, mustDockComponent.componentType)) {
            snapableObjects.push(snapableObject);
            break innerloop;
          }
        }
      }
      // check if there are objects where the current object must not dock
      else if (dbcCurrent.mustNotDock.length > 0) {
        let mustNotDockList = dbcCurrent.mustNotDock;
        let isSnapableObject = true;
        innerloop:
        for (let index = 0; index < mustNotDockList.length; index++) {
          const mustNotDockComponent = mustNotDockList[index];
          if (componentsContain(snapableObject.components, mustNotDockComponent.componentType)) {
            isSnapableObject = false;
            break innerloop;
          }
        }
        if (isSnapableObject) {
          snapableObjects.push(snapableObject);
        }
      }
      // if both lists are empty the usual behaviour of the object is to snap against every other defined object
      // therefore no further checks are needed , snapable objects are equal to the overgiven object array
      else {
        snapableObjects = objects;
        break;
      }
    }
    // this.debugLog("list of snapable objects after docking behaviour filter: ", snapableObjects);
    return snapableObjects;
  }

  // were can we snap our objects?
  getLineGuideStops(skipShape, otherObjects) {
    this.debugLog('getLineGuideStops');
    var vertical = [];
    var horizontal = [];

    otherObjects.forEach((guideItem) => {
      if (guideItem === skipShape) {
        return;
      }
      let skipShapeBox = skipShape.getClientRect();
      var guideItemBox = guideItem.getClientRect();

      // container wall => change ComponentNames.wallComponent to ComponentNames.containerwall
      // applys currently to all walls
      if (componentsContain(guideItem.components, ComponentNames.containerWallComponent)) {
        // this.debugLog("guide stops #IF");
        if ((guideItemBox.height > 20)) {
          if ((skipShapeBox.x + (skipShapeBox.width / 2) > guideItemBox.x + guideItemBox.width)) {
            vertical.push(guideItemBox.x + guideItemBox.width);
          } else {
            vertical.push(guideItemBox.x);
          }
        }
        if ((guideItemBox.width > 20)) {
          if ((skipShapeBox.y + (skipShapeBox.height / 2) > guideItemBox.y + guideItemBox.height)) {
            horizontal.push(guideItemBox.y + guideItemBox.height);
          } else {
            horizontal.push(guideItemBox.y);
          }
        }
      }
      // partition wall
      else if (componentsContain(guideItem.components, ComponentNames.partitionWallComponent)) {
        // this.debugLog("guide stops #ELSE IF");

        if ((skipShapeBox.x + (skipShapeBox.width / 2) > guideItemBox.x + guideItemBox.width)) {
          vertical.push(guideItemBox.x + guideItemBox.width);
        } else {
          vertical.push(guideItemBox.x);
        }

        if ((skipShapeBox.y + (skipShapeBox.height / 2) > guideItemBox.y + guideItemBox.height)) {
          horizontal.push(guideItemBox.y + guideItemBox.height);
        } else {
          horizontal.push(guideItemBox.y);
        }
      }
      else {
        // this.debugLog("guide stops #ELSE");

        // and we can snap to all edges of shapes
        vertical.push(guideItemBox.x + guideItemBox.width);
        vertical.push(guideItemBox.x);
        horizontal.push(guideItemBox.y + guideItemBox.height);
        horizontal.push(guideItemBox.y);
      }
    });
    return {
      vertical: vertical,
      horizontal: horizontal
    };
  }

  // what points of the object will trigger to snapping?
  // it can be just center of the object
  // but we will enable all edges and center
  getObjectSnappingEdges(node: EditorObject) {
    this.debugLog('getObjectSnappingEdges');
    const box = node.getClientRect({});
    const absPos = node.getAbsolutePosition();
    const dbc = getComponentFromSource<DockingBehaviourComponent>(ComponentNames.dockingBehaviorComponent, node.components);
    const offsetbox = dbc.offsetBox;
    const scale = node.getStage().scaleX();

    let x = box.x - (offsetbox.leftOffset * scale);
    let y = box.y - (offsetbox.backOffset * scale);
    let width = box.width + (offsetbox.rightOffset * scale);
    let height = box.height + (offsetbox.frontOffset * scale);

    //return result;
    return {
      vertical: [
        //left edge
        {
          guide: Math.round(box.x),
          offset: Math.round(absPos.x - box.x),
          snap: 'start',
        },
        // right edge
        {
          guide: Math.round(box.x + box.width),
          offset: Math.round(absPos.x - box.x - box.width),
          snap: 'end',
        },
        // left edge with margin
        {
          guide: Math.round(x),
          offset: Math.round(absPos.x - x),
          snap: 'start',
        },
        // right edge with margin
        {
          guide: Math.round(box.x + width),
          offset: Math.round(absPos.x - box.x - width),
          snap: 'end',
        },
      ],
      horizontal: [
        // top edge
        {
          guide: Math.round(box.y),
          offset: Math.round(absPos.y - box.y),
          snap: 'start',
        },
        // bottom edge
        {
          guide: Math.round(box.y + box.height),
          offset: Math.round(absPos.y - box.y - box.height),
          snap: 'end',
        },
        // top edge with margin
        {
          guide: Math.round(y),
          offset: Math.round(absPos.y - y),
          snap: 'start',
        },
        // bottom edge with margin
        {
          guide: Math.round(box.y + height),
          offset: Math.round(absPos.y - box.y - height),
          snap: 'end',
        },
      ]
    };
  }

  // find all snapping possibilities
  getGuides(lineGuideStops, itemBounds) {
    this.debugLog('getGuides');
    const GUIDELINE_OFFSET = 5;
    var resultV = [];
    var resultH = [];

    lineGuideStops.vertical.forEach((lineGuide) => {
      itemBounds.vertical.forEach((itemBound) => {
        var diff = Math.abs(lineGuide - itemBound.guide);
        // if the distance between guide line and object snap point is close we can consider this for snapping
        if (diff < GUIDELINE_OFFSET) {
          resultV.push({
            lineGuide: lineGuide,
            diff: diff,
            snap: itemBound.snap,
            offset: itemBound.offset,
          });
        }
      });
    });

    lineGuideStops.horizontal.forEach((lineGuide) => {
      itemBounds.horizontal.forEach((itemBound) => {
        var diff = Math.abs(lineGuide - itemBound.guide);
        if (diff < GUIDELINE_OFFSET) {
          resultH.push({
            lineGuide: lineGuide,
            diff: diff,
            snap: itemBound.snap,
            offset: itemBound.offset,
          });
        }
      });
    });

    var guides = [];

    // find closest snap
    var minV = resultV.sort((a, b) => a.diff - b.diff)[0];
    var minH = resultH.sort((a, b) => a.diff - b.diff)[0];
    if (minV) {
      guides.push({
        lineGuide: minV.lineGuide,
        offset: minV.offset,
        orientation: 'V',
        snap: minV.snap,
      });
    }
    if (minH) {
      guides.push({
        lineGuide: minH.lineGuide,
        offset: minH.offset,
        orientation: 'H',
        snap: minH.snap,
      });
    }
    return guides;
  }

  drawGuides(guides) {
    this.debugLog('drawGuides');
    guides.forEach((lg) => {
      var layer = this.getLayer(this.editorService.getCurrentLayer());
      const strokeWidth = 10 * layer.scaleX();
      const dashPoint1 = 40 * layer.scaleX();
      const dashPoint2 = 60 * layer.scaleX();
      if (lg.orientation === 'H') {
        var line = new Konva.Line({
          points: [-60000, 0, 60000, 0],
          stroke: 'rgb(0, 161, 255)',
          strokeWidth: strokeWidth,
          name: guide_line_name,
          dash: [dashPoint1, dashPoint2],
        });
        layer.add(line);
        line.setAbsolutePosition({
          x: 0,
          y: lg.lineGuide,
        });
        layer.batchDraw();
      } else if (lg.orientation === 'V') {
        var line = new Konva.Line({
          points: [0, -60000, 0, 60000],
          stroke: 'rgb(0, 161, 255)',
          strokeWidth: strokeWidth,
          name: guide_line_name,
          dash: [dashPoint1, dashPoint2],
        });
        layer.add(line);
        line.setAbsolutePosition({
          x: lg.lineGuide,
          y: 0,
        });
        layer.batchDraw();
      }
    });
  }
}